"use client";

import Field from "@client/components/formik/field";
import Input from "@client/components/formik/input";
import Fieldset from "@client/components/lead-form/components/fieldset";
import { useCMSOverride } from "@client/functions/custom-hooks/useCMSOverride";
import {
	type AddressSuggestion,
	autocompleteAddress,
	checkValidityOfFormValuesAgainstInput,
	formatAutocompleteSuggestion,
	type ValuesType,
} from "@client/functions/smarty";
import Suggestions from "@client/components/smarty/suggestions";
import { postEvent } from "@client/functions/client-actions";
import {
	ONE_LINE_ADDRESS_ERROR_MESSAGE,
	setUserInteractionDataAttribute,
} from "@shared/constants";
import kebabToCamelCase from "@shared/functions/kebabToCamelCase";
import sleep from "@shared/functions/sleep";
import { FormButton } from "@ui/js/components/buttons/form-button";
import clsx from "clsx";
import { useField, useFormikContext } from "formik";
import type React from "react";
import { useEffect, useRef, useState } from "react";

interface Props {
	legendOverride?: string;
	validZipCode?: string;
	cityFromSmarty?: string;
	stateFromSmarty?: string;
	zipCodeFromUser?: string;
}

const INPUT_ID_AND_LABEL_HTMLFOR = "one-line-address";
const FIELD_NAME_SELECTOR = kebabToCamelCase(INPUT_ID_AND_LABEL_HTMLFOR);

export default function Address({
	cityFromSmarty = "",
	legendOverride,
	stateFromSmarty = "",
	validZipCode,
	zipCodeFromUser,
}: Props) {
	const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
	const [displaySuggestions, setDisplaySuggestions] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);
	const wrapperRef = useRef<HTMLFieldSetElement>(null);

	const { submitCount, setFieldValue, setStatus, submitForm, values } =
		useFormikContext();

	const formValues = values as ValuesType;

	// Grabs previous error message from previous submit for addressValidation function -- must be outside function because hooks can only be called top level
	const [oneLineAddressField, oneLineAddressMeta] =
		useField(FIELD_NAME_SELECTOR);

	// If the previous submit had an invalid formatted address
	const previousSubmitHadInvalidFormat =
		oneLineAddressMeta.error === ONE_LINE_ADDRESS_ERROR_MESSAGE;

	useEffect(() => {
		const handleClickOutside: EventListener = (event) => {
			if (
				event.target instanceof HTMLElement &&
				wrapperRef.current &&
				!wrapperRef.current.contains(event.target)
			) {
				setDisplaySuggestions(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	const legendFallback = "What is your address?";
	const legend = useCMSOverride(legendFallback, legendOverride);

	const queryAutocompleteForSuggestions = async (
		query: string,
		e: React.ChangeEvent<HTMLInputElement> | null,
		selected?: string,
	) => {
		// always set the field value for whatever the input is, even if it's an empty string
		setFieldValue(FIELD_NAME_SELECTOR, query);

		// only run lookup if query is not an empty string (or if we have a selected address to expand)
		if (query.length > 0 || selected) {
			try {
				const result = await autocompleteAddress(
					query,
					validZipCode,
					5, // maxResults
					selected,
				);

				if (result.error) {
					postEvent("error", {
						error: {
							error_message: result.error,
							error_object: "Lead Form",
							error_type: "smarty_validation",
							path: window.location.href,
						},
					});
				}
				setDisplaySuggestions(result.suggestions.length > 0);
				setSuggestions(result.suggestions);
			} catch (error) {
				postEvent("error", {
					error: {
						error_message:
							"Smarty lookup failed - see 'raw_error' in this error event object for more information",
						error_object: "Lead Form",
						error_type: "smarty_validation",
						path: window.location.href,
						raw_error: error,
					},
				});
			}
		}
	};

	const setFormValues = (address: AddressSuggestion) => {
		setFieldValue(
			"streetAddress",
			address.streetLine === "invalid" || !address.streetLine
				? formValues.oneLineAddress
				: (address.streetLine || "") +
						(address.secondary ? ` ${address.secondary}` : ""),
		);
		setFieldValue(
			"city",
			address.city === "invalid" || !address.city
				? cityFromSmarty
				: address.city,
		);
		setFieldValue(
			"state",
			address.state === "XX" || !address.state
				? stateFromSmarty
				: address.state,
		);
		setFieldValue(
			"zipCode",
			address.zipcode === "11111" || !address.zipcode
				? zipCodeFromUser
				: address.zipcode,
		);
	};

	const clearFormValues = () => {
		setFieldValue("streetAddress", "");
		setFieldValue("city", "");
		setFieldValue("state", "");
		setFieldValue("zipCode", "");
	};

	const selectSuggestion = async (
		suggestion: AddressSuggestion,
		bail: boolean = false,
	) => {
		if (bail) {
			setDisplaySuggestions(false);
			return;
		}

		if (suggestion.entries > 1) {
			// Format suggestion for display (without entries count)
			const displayAddress = await formatAutocompleteSuggestion(suggestion);
			// Format suggestion for `selected` parameter (with entries count to trigger expansion)
			const selectedAddress = await formatAutocompleteSuggestion(
				suggestion,
				true,
			);
			// Update field value to show the selected address
			setFieldValue(FIELD_NAME_SELECTOR, displayAddress);
			if (inputRef.current) {
				inputRef.current.value = displayAddress;
			}
			// Query with the selected address to expand secondary entries
			await queryAutocompleteForSuggestions(
				displayAddress,
				null,
				selectedAddress,
			);
			// Keep focus on input after expanding secondary entries
			inputRef.current?.focus();
		} else {
			postEvent("smarty_suggestion_selected", {
				event: {
					event_message:
						"User selected a Smarty Streets suggestion from the suggestions list.",
					event_type: "smarty_suggestion_selected",
					event_object: "Address smarty streets suggestion selection",
					suggestion: { ...suggestion } as any,
				},
			});
			setFormValues(suggestion);

			const formattedAddress = await formatAutocompleteSuggestion(suggestion);
			setFieldValue(FIELD_NAME_SELECTOR, formattedAddress);

			if (inputRef.current) {
				inputRef.current.value = formattedAddress;
				inputRef.current.focus();
			}
			setDisplaySuggestions(false);
		}
	};

	const invalidAddressObject = {
		streetLine: "invalid",
		secondary: "",
		entries: 0,
		city: "invalid",
		state: "XX",
		zipcode: zipCodeFromUser || "11111",
	};

	async function onSubmitAddress() {
		if (
			formValues.oneLineAddress &&
			!(await checkValidityOfFormValuesAgainstInput(formValues))
		) {
			try {
				const result = await autocompleteAddress(formValues.oneLineAddress);
				const suggestions = result.suggestions;

				if (
					suggestions.length === 1 &&
					formValues.oneLineAddress.includes(suggestions[0].zipcode)
				) {
					setFormValues(suggestions[0]);
					const formattedAddress = await formatAutocompleteSuggestion(
						suggestions[0],
					);
					postEvent("warning", {
						error: {
							error_message:
								"Valid address provided, but no suggestion selected; valid address form values set via autocomplete verification",
							error_object: "Lead Form",
							error_type: "smarty_validation",
							path: window.location.href,
						},
					});
					setFieldValue(FIELD_NAME_SELECTOR, formattedAddress);
				} else {
					submitCount > 0
						? setFormValues(invalidAddressObject)
						: clearFormValues();
				}

				await sleep(300);
				submitForm();
			} catch (error) {
				postEvent("error", {
					error: {
						error_message:
							"Smarty lookup failed - see 'raw_error' in this error event object for more information",
						error_object: "Lead Form",
						error_type: "smarty_validation",
						path: window.location.href,
						raw_error: error,
					},
				});
				await sleep(300);
				submitForm();
			}
		} else {
			submitForm();
		}
	}

	async function addressValidation() {
		// The error message to display to the user
		let errorMessage: string | undefined;

		// If submit count is <0 and the previous submit did not have an invalid format, do not show validation error and let field be submitted
		if (
			submitCount > 0 &&
			!previousSubmitHadInvalidFormat &&
			formValues.oneLineAddress
		) {
			if (formValues.streetAddress === "invalid") {
				postEvent("warning", {
					error: {
						error_message: `User has successfully submitted an address not verified by Smarty Autocomplete`,
						error_object: "Lead Form",
						error_type: "smarty_validation",
						path: window.location.href,
						invalid_address: formValues.oneLineAddress,
					},
				});
				return ""; // No validation error
			}
			if (!formValues.streetAddress) {
				postEvent("error", {
					error: {
						error_message: `Internal parsing has submitted a blank street address`,
						error_object: "Lead Form",
						error_type: "smarty_validation",
						path: window.location.href,
						invalid_address: formValues.oneLineAddress,
					},
				});
				setStatus("warning");
				errorMessage =
					"The entered address appears to be invalid.  Please verify it is correct.";
			}
		}

		// Reset the lead form status via Formik
		setStatus("");
		// If the streetAddress field is populated, return no error
		if (formValues.streetAddress) {
			return "";
		} else if (!formValues.oneLineAddress) {
			// Set the lead form status to "error" via Formik
			setStatus("error");
			postEvent("error", {
				error: {
					error_message: `User has submitted a blank address form`,
					error_object: "Lead Form",
					error_type: "smarty_validation",
					path: window.location.href,
				},
			});

			errorMessage = ONE_LINE_ADDRESS_ERROR_MESSAGE;
			return errorMessage;
		} else {
			// Set the lead form status to "warning" via Formik
			setStatus("warning");

			postEvent("warning", {
				error: {
					error_message: `User has submitted an address not verified by Smarty Autocomplete; warning message shown`,
					error_object: "Lead Form",
					error_type: "smarty_validation",
					path: window.location.href,
					invalid_address: formValues.oneLineAddress,
				},
			});

			errorMessage =
				"The entered address appears to be invalid.  Please verify it is correct.";
			await sleep(300);
		}

		return errorMessage;
	}

	return (
		<>
			<Input type="hidden" name="streetAddress" id="street-address" />
			<Input type="hidden" name="city" id="city" />
			<Input type="hidden" name="state" id="state" />
			<Input type="hidden" name="zipCode" id="zip-code" />
			<Fieldset
				className={clsx({
					"rounded-b-none!": displaySuggestions,
				})}
				dialog="We request your address for three main reasons: to verify your identity, to conduct an accurate soft credit pull without affecting your score, and to match you with the best financial solutions. Your privacy is paramount to us; all data is encrypted and used with the utmost care."
				legend={legend}
				ref={wrapperRef}
			>
				<Field
					{...setUserInteractionDataAttribute(
						`address_field_${INPUT_ID_AND_LABEL_HTMLFOR}`,
					)}
					className={clsx({
						"rounded-b-none!": displaySuggestions,
					})}
					id={INPUT_ID_AND_LABEL_HTMLFOR}
					name={FIELD_NAME_SELECTOR}
					label="Street Address"
					validate={addressValidation}
				>
					<Input
						className={clsx({
							"rounded-b-none!": displaySuggestions,
						})}
						id={INPUT_ID_AND_LABEL_HTMLFOR}
						name={FIELD_NAME_SELECTOR}
						ref={inputRef}
						onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
							const suggestion = await queryAutocompleteForSuggestions(
								e.target.value,
								e,
							);
							return suggestion;
						}}
						autoComplete="off"
					/>
				</Field>
				{displaySuggestions && (
					<Suggestions
						{...setUserInteractionDataAttribute(
							`address_suggestions_${INPUT_ID_AND_LABEL_HTMLFOR}`,
						)}
						isOpen={displaySuggestions}
						ariaControlsId={INPUT_ID_AND_LABEL_HTMLFOR}
						suggestions={suggestions}
						selectSuggestion={selectSuggestion}
					/>
				)}
			</Fieldset>
			<FormButton
				{...setUserInteractionDataAttribute("address_submit-button")}
				// This is necessary to prevent default form submission behavior
				type="button"
				onClick={onSubmitAddress}
			/>
		</>
	);
}
